import "./FetchFilterBar.scss";
import { Button, ButtonType } from "../../Components";
import { Category, FilterPaneAdvancedState } from "@blisspointmedia/bpm-types/dist/FilterPane";
import {
  filterTokenizer,
  makeSuggestionGenerator,
} from "@blisspointmedia/bpm-types/dist/advancedFilterParser";
import { Form } from "react-bootstrap";
import { StateSetter } from "../../utils/types";
import * as R from "ramda";
import React, { useCallback, useMemo, useRef, useState } from "react";

import {} from "react-icons/md";

interface FilterPaneAdvancedProps {
  categories: Category[];
  compileError: string;
  readonly?: boolean;
  setEditing: StateSetter<boolean>;
  setState: StateSetter<FilterPaneAdvancedState>;
  state: string;
  showAdvancedHeader?: boolean;
}

const suggestionHelpMap = {
  "=": "= (equal to)",
  "!=": "!= (not equal to)",
  "~": "~ (like)",
  "!~": "!~ (not like)",
};

export const FilterPaneAdvanced: React.FC<FilterPaneAdvancedProps> = ({
  categories,
  compileError,
  readonly,
  setEditing,
  setState,
  state,
  showAdvancedHeader,
}) => {
  const advancedText = state;
  const setAdvancedTextRaw = useCallback((text: string) => setState(text), [setState]);

  const [cursorPosition, setCursorPosition] = useState(0);

  const textAreaRef = useRef<HTMLTextAreaElement>(null);

  const updateCursorPosition = useCallback(() => {
    const pos = textAreaRef.current?.selectionStart;
    if (!R.isNil(pos)) {
      setCursorPosition(pos);
    }
  }, []);

  const setAdvancedText = useCallback(
    (text: string) => {
      setAdvancedTextRaw(text);
      updateCursorPosition();
    },
    [updateCursorPosition, setAdvancedTextRaw]
  );

  const forceCursorPosition = useCallback((pos: number) => {
    if (textAreaRef.current) {
      textAreaRef.current.setSelectionRange(pos, pos);
      setCursorPosition(pos);
    }
  }, []);

  const suggestionGenerator = useMemo(() => makeSuggestionGenerator(categories), [categories]);

  const suggestionObject = useMemo(() => {
    const tokens = filterTokenizer(advancedText);
    const suggestionObject = suggestionGenerator(advancedText, tokens, cursorPosition);
    return suggestionObject;
  }, [advancedText, suggestionGenerator, cursorPosition]);

  const selectingLikeValue = useMemo(() => {
    const tokens = filterTokenizer(advancedText);
    const lastCompleteToken = tokens[tokens.length - 2];
    const isLikeComp =
      lastCompleteToken?.type === "comp" && ["~", "!~"].includes(lastCompleteToken?.value);

    if (!isLikeComp || R.isNil(lastCompleteToken?.end)) {
      return false;
    }

    const textAfterComp = advancedText.slice(lastCompleteToken.end, cursorPosition).trim();

    return textAfterComp.length > 0;
  }, [advancedText, cursorPosition]);

  const defaultSuggestions = suggestionObject.suggestions;

  const logicalSuggestions = ["and", "or"];
  const showLogicalSuggestions =
    selectingLikeValue && logicalSuggestions.some(s => !defaultSuggestions.includes(s));

  const insertText = useCallback(
    (text: string) => {
      const [newText, newPos] = suggestionObject.inserter(text);
      setAdvancedText(newText);
      forceCursorPosition(newPos);
    },
    [forceCursorPosition, suggestionObject, setAdvancedText]
  );

  const appendAtEnd = (textToAppend: string) => {
    const isCursorAtEnd = cursorPosition === advancedText.length;
    if (isCursorAtEnd) {
      const newText = `${advancedText} ${textToAppend} `;
      setAdvancedText(newText);
      forceCursorPosition(newText.length);
    } else {
      insertText(textToAppend);
    }
  };

  const renderSuggestions = (suggestions: string[], onClick: (suggestion: string) => void) => {
    return suggestions.map(suggestion => (
      <Button
        key={suggestion}
        background="light"
        className="suggestion"
        design="secondary"
        size="sm"
        type={ButtonType.FILLED}
        onClick={() => onClick(suggestion)}
      >
        {suggestionHelpMap[suggestion] || suggestion || '""'}
      </Button>
    ));
  };

  return (
    <div className="filterPaneBody">
      {showAdvancedHeader !== false && (
        <div className="filterPaneAdvancedHeader">Advanced Filter</div>
      )}
      <div className="filterPaneAdvancedBoxContainer">
        <Form.Control
          disabled={readonly}
          spellCheck={false}
          ref={textAreaRef}
          className="filterPaneAdvancedBox"
          as="textarea"
          rows={6}
          value={advancedText}
          onKeyUp={updateCursorPosition}
          onClick={updateCursorPosition}
          onChange={e => setAdvancedText(e.currentTarget.value)}
          onFocus={() => setEditing(true)}
          onBlur={() => setEditing(false)}
        />
      </div>
      {compileError && <div className="compileError">{compileError}</div>}
      {showLogicalSuggestions && (
        <div className="suggestionList">
          {renderSuggestions(
            logicalSuggestions.filter(s => !defaultSuggestions.includes(s)),
            appendAtEnd
          )}
        </div>
      )}

      {!readonly && (
        <div className="suggestionList">
          {renderSuggestions(suggestionObject.suggestions, insertText)}
        </div>
      )}
    </div>
  );
};

export default FilterPaneAdvanced;
